import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useSaveBankDetails, useVerifyIfsc, useFetchBankDetails } from "../api";
import {
  IFSC_REGEX,
  ACCOUNT_NUMBER_REGEX,
  MOBILE_REGEX,
  IFSC_VERIFY_DEBOUNCE_MS,
} from "../utils/constants";

/**
 * Owns state, validation, IFSC auto-verification, and save logic for the
 * bank-details form — shared by EditBankDetailsModal (standalone) and
 * EditPanAndBankDetailsModal (combined), which is why it's hoisted to
 * services/hooks instead of co-located.
 * POST/PUT /mentor/:mentorId/kyc/bank and POST /mentor/kyc/ifsc-verify
 * (see services/api: useSaveBankDetails, useVerifyIfsc).
 * @param {Object} params
 * @param {string} params.userId
 * @param {Object} [params.currentDetails] - Prefill data (accountHolderName, mobileNumber, ifscCode, bankName, branchName, accountType).
 * @param {boolean} params.isOpen - The form resets whenever the owning modal opens.
 * @returns {{ state, loading, derived, handlers, mutators }}
 */
export const useBankDetailsForm = ({ userId, currentDetails = {}, isOpen }) => {
  const chequeInputRef = useRef(null);
  // Set whenever ifscCode changes because of the reset effect (prefill), not because
  // the mentor typed something — lets the verification effect below skip re-verifying
  // an IFSC that's already known-good instead of wiping bankName/branchName while it re-checks.
  const skipIfscVerifyRef = useRef(false);
  // Snapshot of the prefilled values, captured on open — lets isFormComplete allow a save
  // on edit as soon as anything actually changed, instead of re-demanding every field.
  const initialValuesRef = useRef({});

  // The fields a mentor actually fills in, grouped into one object instead of one useState
  // per field — the meta/UI state below (verification status, file, errors) stays separate
  // since it isn't part of "bank details" and changes on a different cadence.
  const [bankDetails, setBankDetails] = useState({
    accountHolderName: "",
    mobileNumber: "",
    ifscCode: "",
    bankName: "",
    branchName: "",
    accountNumber: "",
    confirmAccountNumber: "",
    accountType: "savings",
  });
  const {
    accountHolderName,
    mobileNumber,
    ifscCode,
    bankName,
    branchName,
    accountNumber,
    confirmAccountNumber,
    accountType,
  } = bankDetails;

  const [ifscStatus, setIfscStatus] = useState("idle"); // idle | verifying | success | failed
  const [ifscError, setIfscError] = useState("");
  const [chequeFile, setChequeFile] = useState(null);
  const [existingBankProofUrl, setExistingBankProofUrl] = useState(null);
  const [existingBankProofName, setExistingBankProofName] = useState(null);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [verificationStatus, setVerificationStatus] = useState(null);

  const isEditingExisting = Object.keys(currentDetails).length > 0;

  const { mutate: saveBankDetails, loading: isSaving } =
    useSaveBankDetails(userId);
  const { mutate: verifyIfsc } = useVerifyIfsc();

  const { mutate: fetchFreshBankDetails } = useFetchBankDetails(userId, {
    manual: true,
  });

  const viewDocument = async () => {
    const data = await fetchFreshBankDetails();
    const freshUrl = data?.documents?.find(
      (doc) => doc.documentType === "bank_proof",
    )?.url;
    if (freshUrl) window.open(freshUrl, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    if (isOpen) {
      const initial = {
        accountHolderName: currentDetails.accountHolderName || "",
        mobileNumber: currentDetails.mobileNumber || "",
        ifscCode: currentDetails.ifscCode || "",
        accountNumber: currentDetails.accountNumber || "",
        accountType: currentDetails.accountType || "savings",
      };
      initialValuesRef.current = initial;

      setBankDetails({
        accountHolderName: initial.accountHolderName,
        mobileNumber: initial.mobileNumber,
        ifscCode: initial.ifscCode,
        bankName: currentDetails.bankName || "",
        branchName: currentDetails.branchName || "",
        accountNumber: initial.accountNumber,
        confirmAccountNumber: initial.accountNumber,
        accountType: initial.accountType,
      });
      setIfscError("");

      const hasKnownBankName = !!(
        currentDetails.ifscCode && currentDetails.bankName
      );
      setIfscStatus(hasKnownBankName ? "success" : "idle");
      skipIfscVerifyRef.current = hasKnownBankName;
      setChequeFile(null);
      setExistingBankProofUrl(currentDetails.bankProofDocumentUrl || null);
      setExistingBankProofName(currentDetails.bankProofDocumentName || null);
      setErrors({});
      setApiError("");
      setVerificationStatus(null);
    }
    // Deliberately NOT depending on currentDetails: EditDetailsButton recomputes it as a
    // brand-new object on every one of its own re-renders (mapBankDetails(...) returns a
    // fresh literal each call), so keying this on currentDetails would re-run the reset
    // — silently wiping whatever the mentor had typed — any time something elsewhere on
    // the page caused a re-render while this modal was sitting open. Only isOpen actually
    // flipping false->true means "a new edit session started."
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (skipIfscVerifyRef.current) {
      skipIfscVerifyRef.current = false;
      return;
    }

    const trimmed = ifscCode.trim().toUpperCase();
    const setBankAndBranch = (bank, branch) =>
      setBankDetails((prev) => ({ ...prev, bankName: bank, branchName: branch }));

    if (!IFSC_REGEX.test(trimmed)) {
      setIfscStatus("idle");
      setBankAndBranch("", "");
      return;
    }

    setIfscStatus("verifying");
    const timeoutId = setTimeout(() => {
      verifyIfsc({
        data: { ifscCode: trimmed },
        onSuccess: (data) => {
          if (data?.success === false) {
            setIfscStatus("failed");
            setIfscError(data?.msg || "Invalid IFSC code");
            setBankAndBranch("", "");
            return;
          }
          setIfscStatus("success");
          setBankAndBranch(data?.bankName || "", data?.branchName || "");
        },
        onError: () => {
          setIfscStatus("failed");
          setIfscError("Invalid IFSC code");
          setBankAndBranch("", "");
        },
      });
    }, IFSC_VERIFY_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ifscCode]);

  const clearError = (field) => {
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const setBankDetailsField = (field, value) =>
    setBankDetails((prev) => ({ ...prev, [field]: value }));

  const setAccountHolderName = (value) => {
    setBankDetailsField("accountHolderName", value);
    clearError("accountHolderName");
  };

  const setMobileNumber = (value) => {
    setBankDetailsField("mobileNumber", value.replace(/\D/g, "").slice(0, 10));
    clearError("mobileNumber");
  };

  const setIfscCode = (value) => {
    setBankDetailsField("ifscCode", value.toUpperCase());
    clearError("ifscCode");
  };

  const setAccountNumber = (value) => {
    setBankDetailsField("accountNumber", value.replace(/\D/g, ""));
    clearError("accountNumber");
  };

  const setConfirmAccountNumber = (value) => {
    setBankDetailsField("confirmAccountNumber", value.replace(/\D/g, ""));
    clearError("confirmAccountNumber");
  };

  const setAccountType = (value) => setBankDetailsField("accountType", value);

  const handleChequeFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setChequeFile(file);
    clearError("chequeFile");
    e.target.value = null;
  };

  const isDirty =
    accountHolderName !== initialValuesRef.current.accountHolderName ||
    mobileNumber !== initialValuesRef.current.mobileNumber ||
    ifscCode !== initialValuesRef.current.ifscCode ||
    accountNumber !== initialValuesRef.current.accountNumber ||
    accountType !== initialValuesRef.current.accountType ||
    !!chequeFile;

  const accountNumberChanged =
    accountNumber !== initialValuesRef.current.accountNumber;

  const accountNumbersMatch =
    !!confirmAccountNumber &&
    !!accountNumber &&
    confirmAccountNumber === accountNumber;
  const accountNumbersMismatch =
    !!confirmAccountNumber &&
    !!accountNumber &&
    confirmAccountNumber !== accountNumber;
  const showIfscFormatError =
    !!ifscCode.trim() && !IFSC_REGEX.test(ifscCode.trim());

  const validate = () => {
    const newErrors = {};
    if (!accountHolderName.trim())
      newErrors.accountHolderName = "Account holder name is required";
    if (!mobileNumber.trim()) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (!MOBILE_REGEX.test(mobileNumber.trim())) {
      newErrors.mobileNumber = "Enter a valid 10-digit mobile number";
    }
    if (!ifscCode.trim()) {
      newErrors.ifscCode = "IFSC code is required";
    } else if (!IFSC_REGEX.test(ifscCode.trim())) {
      newErrors.ifscCode = "Invalid IFSC code";
    } else if (ifscStatus !== "success") {
      newErrors.ifscCode = "Please wait for IFSC verification to complete";
    }
    if (!accountNumber.trim()) {
      newErrors.accountNumber = "Account number is required";
    } else if (!ACCOUNT_NUMBER_REGEX.test(accountNumber.trim())) {
      newErrors.accountNumber = "Enter a valid account number";
    }

    if (!isEditingExisting || accountNumberChanged) {
      if (!confirmAccountNumber.trim()) {
        newErrors.confirmAccountNumber = "Please confirm the account number";
      } else if (confirmAccountNumber.trim() !== accountNumber.trim()) {
        newErrors.confirmAccountNumber = "Account numbers do not match";
      }
    }
    if (!isEditingExisting && !chequeFile)
      newErrors.chequeFile =
        "Please upload a cancelled cheque or passbook front page";
    setErrors(newErrors);
    return newErrors;
  };

  const submit = async () => {
    setApiError("");
    setVerificationStatus(null);

    const formData = new FormData();
    formData.append("accountHolderName", accountHolderName.trim());
    formData.append("mobileNumber", mobileNumber.trim());
    formData.append("ifscCode", ifscCode.trim().toUpperCase());
    formData.append("bankName", bankName);
    formData.append("branchName", branchName);
    formData.append("accountNumber", accountNumber.trim());
    formData.append("accountType", accountType);
    if (chequeFile) formData.append("bankProofFile", chequeFile);

    let requestOk = false;
    let verified = false;
    let data;
    await saveBankDetails({
      method: isEditingExisting ? "put" : "post",
      data: formData,
      config: { headers: { "Content-Type": "multipart/form-data" } },
      onSuccess: (resData) => {
        requestOk = true;
        data = resData;
        setVerificationStatus(resData?.verificationStatus || null);
        verified = resData?.verificationStatus === "verified";
      },
      onError: () => {
        setApiError(
          "Something went wrong while saving your details. Please try again.",
        );
      },
    });
    return { requestOk, verified, data };
  };

  // Standalone-modal flow: validate, submit, and toast/close on the same rules
  // EditBankDetailsModal used before this hook existed.
  const handleSave = async ({ onSaveSuccess, onClose } = {}) => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) return;

    const { requestOk, verified, data } = await submit();

    if (requestOk) {
      if (onSaveSuccess) onSaveSuccess(data);
      if (verified) {
        toast.success("Bank details saved");
        if (onClose) onClose();
      }
    } else {
      toast.error("Failed to save bank details");
    }
  };

  const isFormComplete = isEditingExisting
    ? isDirty && (!accountNumberChanged || accountNumbersMatch)
    : !!accountHolderName.trim() &&
      !!mobileNumber.trim() &&
      !!ifscCode.trim() &&
      !!accountNumber.trim() &&
      !!confirmAccountNumber.trim() &&
      !!chequeFile;

  return {
    state: {
      accountHolderName,
      mobileNumber,
      ifscCode,
      bankName,
      branchName,
      ifscStatus,
      ifscError,
      accountNumber,
      confirmAccountNumber,
      accountType,
      chequeFile,
      existingBankProofUrl,
      existingBankProofName,
      errors,
      apiError,
      verificationStatus,
      chequeInputRef,
    },
    loading: { isSaving },
    derived: {
      isFormComplete,
      isDirty,
      accountNumbersMatch,
      accountNumbersMismatch,
      showIfscFormatError,
    },
    handlers: {
      setAccountHolderName,
      setMobileNumber,
      setIfscCode,
      setAccountNumber,
      setConfirmAccountNumber,
      setAccountType,
      handleChequeFileSelect,
      viewDocument,
    },
    mutators: { validate, submit, handleSave },
  };
};
